import { useCallback, useMemo } from 'react'
import { Box, Stack } from '@mui/material'

import { SoapInlineAI } from '@/apps/latest/components/molecules/SoapInlineAI'
import { RecordAttachmentsSection } from '@/apps/latest/features/yakureki/components/RecordAttachmentsSection'
import {
  SOAP_IMPROVEMENT_CATEGORIES,
  type SoapImprovementSuggestion,
} from '@/apps/latest/features/formatter/types/soapImprovement'
import { useSOAPImprovement } from '@/features/formatter'
import { useSettingQuery } from '@/features/auth'
import { useToast } from '@/shared/contexts/ToastContext'
import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { type YakurekiDetail } from '@/shared/types/yakurekiCore'
import { type SoapContent } from '@/shared/types/yakurekiSoap'

interface Props {
  event: YakurekiDetail
  reloadEvent: () => Promise<void>
  // AI で改善の後 (変わった SOAP を受けて読み直す)
  onAiUpdate: (updatedSoap: SoapContent) => Promise<void>
  // 資料の節の見出しの横の ⓘ (まだ SOAP に入っていない資料を読む差分更新)
  onReflect: (ids: string[]) => Promise<void>
}

// SOAP 本体の下は 2 つ: 「文書を直す AI の口」= ✦ AI で改善 の 1 行 (AI の改善提案はその中) と、
// 資料の節 (見出し「資料 ⓘ」が開いたまま。β。最も稀なので最後。間に罫線は引かない)。
// (design-book ADR-0055・hub log/108 決定 43 → 44 → 45 → 46。確認事項は録音前の pane だけ。改善提案の引き出しは AI の口の中)
// SOAP body から上罫線 + 軽い余白で分離し、AI の口と資料の節の間は SOAP の節と節と同じ 16px (spacing 2)。
// 節の中 (見出しと行) は 2px で詰める = 分離は余白の差、凝集は詰め (決定 46)。
export const SoapMetaBlocks = ({
  event,
  reloadEvent,
  onAiUpdate,
  onReflect,
}: Props) => {
  const { showToast } = useToast()

  // 店の設定 (ui_config.improvements_expanded)「改善提案を最初から開く」= 押していない提案があれば
  // AI の口を開いて始める。画面上の開閉はその場限りで、設定 (既定) は書き換えない。設定不在時は false。
  const { data: setting } = useSettingQuery()
  const defaultOpen = setting?.ui_config?.improvements_expanded ?? false

  const { suggestions: soapImprovements, applySuggestion } = useSOAPImprovement(
    event.id
  )
  // category 順序だけ保った flat list (= 「基本」「加算」 等の section heading 不要).
  const orderedImprovements = useMemo(
    () =>
      SOAP_IMPROVEMENT_CATEGORIES.flatMap((category) =>
        soapImprovements.filter((s) => s.category === category.id)
      ),
    [soapImprovements]
  )

  const handleApplyImprovement = useCallback(
    async (suggestion: SoapImprovementSuggestion) => {
      const result = await applySuggestion(suggestion.id)
      if (result?.success) {
        showToast('提案を反映しました', 'success')
        await reloadEvent()
      } else if (result) {
        showToast('提案の反映に失敗しました', 'error')
      }
    },
    [applySuggestion, reloadEvent, showToast]
  )

  return (
    <Box
      sx={{
        mt: 2,
        pt: 1.5,
        borderTop: `1px solid ${colorTokens.border.default}`,
      }}
    >
      <Stack spacing={2}>
        {/* AI 修正 = Notion inline (Cmd+J 風). 1 行 prompt が展開してその場で生成 / 反映. modal / chat panel は廃止.
            文章だけで資料は送らない (資料を入れるのは下の節の ⓘ). */}
        <SoapInlineAI
          yakurekiId={event.id}
          onSoapUpdate={onAiUpdate}
          suggestions={orderedImprovements}
          onApplySuggestion={handleApplyImprovement}
          defaultOpen={defaultOpen}
        />

        {/* 資料 = 完了後に紙を足す口と 1 件 1 行の一覧 (β)。見出し「資料」が開いたままの節。
            まだ SOAP に入っていない資料は見出しの横の ⓘ を押して差分更新 (design-book 04-apps/kakeru.md「資料添付 (薬剤師)」). */}
        <RecordAttachmentsSection event={event} onReflect={onReflect} />
      </Stack>
    </Box>
  )
}
